import { useQuery } from '@tanstack/react-query';
import { api, unwrap, type Schemas } from '../../../api/client';
import { useAuth } from '../../../auth/AuthProvider';
import { useShop } from '../../../shop/ShopProvider';
import type { PricingSettings } from './cart';

export type SalesInvoice = Schemas['SalesInvoice'];
export type SalesInvoiceRow = Schemas['SalesInvoiceList']['data'][number];
export type SalesOrder = Schemas['SalesOrder'];
export type SalesOrderRow = Schemas['SalesOrderList']['data'][number];
export type Delivery = Schemas['Delivery'];
export type DeliveryRow = Schemas['DeliveryList']['data'][number];
export type CounterSale = Schemas['CounterSale'];
export type PaymentMethod = Schemas['PaymentMethod'];
export type TaxRate = Schemas['TaxRate'];
export type VariantSummary = Schemas['VariantSummary'];
export type Product = Schemas['Product'];

/** Query keys owned by the sales feature (invalidate the whole family after a mutation). */
export const salesKeys = {
  invoices: ['sales-invoices'] as const,
  orders: ['sales-orders'] as const,
  deliveries: ['deliveries'] as const,
};

/** Every list or balance a posted sale changes. */
export const afterSaleKeys = [
  salesKeys.invoices,
  salesKeys.orders,
  salesKeys.deliveries,
  ['payments'],
  ['contacts'],
  ['stock'],
  ['reports'],
] as const;

export function usePaymentMethods() {
  return useQuery({
    queryKey: ['payment-methods'],
    queryFn: () => unwrap(api.GET('/payment-methods')),
    staleTime: 5 * 60_000,
    select: (d) => d.data.filter((m) => m.isActive),
  });
}

/** Active tax rates; empty when tax is off or the user cannot read settings. */
export function useTaxRates() {
  const { can } = useAuth();
  const { settings } = useShop();
  const enabled = !!settings?.taxEnabled && can('settings.view');
  return useQuery({
    queryKey: ['tax-rates'],
    queryFn: () => unwrap(api.GET('/tax-rates')),
    staleTime: 5 * 60_000,
    enabled,
    select: (d) => d.data.filter((t) => t.isActive),
  });
}

/** The shop's pricing rules for live totals. */
export function usePricing(): PricingSettings {
  const { settings, format } = useShop();
  return {
    decimals: format.currencyDecimals,
    taxEnabled: settings?.taxEnabled ?? false,
    pricesIncludeTax: settings?.pricesIncludeTax ?? false,
  };
}

/** Product with its units (for the unit selector), cached per product. */
export function productQuery(productId: string) {
  return {
    queryKey: ['products', productId],
    queryFn: () => unwrap(api.GET('/products/{id}', { params: { path: { id: productId } } })),
    staleTime: 60_000,
  };
}

export interface UnitOption {
  unitId: string;
  symbol: string;
  factor: string;
  isDefaultSale: boolean;
}

/** Base unit first, then alternate units. */
export function unitOptions(product: Product): UnitOption[] {
  const base: UnitOption = {
    unitId: product.baseUnit.id,
    symbol: product.baseUnit.symbol,
    factor: '1',
    isDefaultSale: !product.units.some((u) => u.isDefaultSale),
  };
  return [
    base,
    ...product.units
      .filter((u) => u.unitId !== product.baseUnit.id)
      .map((u) => ({
        unitId: u.unitId,
        symbol: u.symbol,
        factor: u.factor,
        isDefaultSale: u.isDefaultSale,
      })),
  ];
}

/**
 * Units of a document line's product (the variant is found by its exact SKU when the product is
 * not known), base unit first. Empty while loading.
 */
export function useVariantUnits(line: {
  sku: string;
  variantId: string;
  productId?: string;
}): UnitOption[] {
  const variant = useQuery({
    // There is no GET /variants/{id}; the exact SKU lookup gives the product.
    queryKey: ['variants', 'lookup', line.sku],
    queryFn: () => unwrap(api.GET('/variants/lookup', { params: { query: { code: line.sku } } })),
    enabled: !line.productId,
    staleTime: 5 * 60_000,
  });
  const productId =
    line.productId ||
    (variant.data && variant.data.id === line.variantId ? variant.data.productId : '');
  const product = useQuery({ ...productQuery(productId), enabled: !!productId });
  return product.data ? unitOptions(product.data) : [];
}

/** Today's business date in the shop's time zone (YYYY-MM-DD). */
export function shopToday(timeZone: string): string {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

export function addDays(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** The currency's narrow symbol, e.g. "Rs". */
export function currencySymbol(f: { currencyCode: string; locale: string }): string {
  try {
    return (
      new Intl.NumberFormat(f.locale, {
        style: 'currency',
        currency: f.currencyCode,
        currencyDisplay: 'narrowSymbol',
      })
        .formatToParts(0)
        .find((p) => p.type === 'currency')?.value ?? f.currencyCode
    );
  } catch {
    return f.currencyCode;
  }
}

/** Header for POST endpoints (the client adds one anyway; pass a fixed key to make a retry safe). */
export function idem(key: string = crypto.randomUUID()) {
  return { 'idempotency-key': key };
}

export const salesInvoiceStatuses = ['POSTED', 'CANCELLED'] as const;
export const salesOrderStatuses = [
  'DRAFT',
  'CONFIRMED',
  'PARTIALLY_DELIVERED',
  'DELIVERED',
  'CLOSED',
  'CANCELLED',
] as const;

/** Locations goods can be sold from (default first). */
export function useSellableLocations() {
  return useQuery({
    queryKey: ['locations'],
    queryFn: () => unwrap(api.GET('/locations')),
    staleTime: 5 * 60_000,
    select: (d) =>
      d.data
        .filter((l) => l.isActive && l.isSellable)
        .sort((a, b) => Number(b.isDefault) - Number(a.isDefault)),
  });
}
